import React, { type ChangeEvent, useCallback, useState } from "react";
import Debug from "../Debug";
import { useDebouncedEffect } from "../hooks";
import { useCommonSelector } from "../redux/commonStore";
import type { A2DContext, DealerResultType } from "../utils/dealerConstants";

// This will likely expand over time depending on requirements.
// Not sure what the source is of these keys, seems like a DXP-specific thing.
export enum AemDealerFinderService {
    TradeInAppraisal = "Trade In Appraisal",
}

export type DealerFinderSettingsType = {
    showResultsOnPageLoad?: boolean; // show-results-on-page-load - Defaults to true
};

type PropsType = {
    onDealerSelect: (dealer: DealerResultType | null) => void;
    dealerSearchEndpoint: string;
    dealerDetailEndpoint: string;
    previouslySelectedDealer?: DealerResultType | null;
    serviceFilter?: AemDealerFinderService; // Used to filter dealers based on available services.
    settings?: DealerFinderSettingsType;
    a2dContext?: A2DContext;
};

/**
 * Common component that renders Dealer Finder custom web component from Zaidan team
 * Also contains logic for the standalone fallback.
 * Can be used in both Toyota and Lexus variants since it doesn't use brand specific styles/classnames.
 */
const AemDealerFinder = (props: PropsType): JSX.Element => {
    const { dealerSearchEndpoint, dealerDetailEndpoint, previouslySelectedDealer, serviceFilter, settings } = props;
    const { a2dContext } = props;
    const { onDealerSelect } = props;
    const { showResultsOnPageLoad = true } = settings || {};

    const commonSettings = useCommonSelector((state) => state.commonSettings);

    // In theory this could be useReducer, but there doesn't seem to be a big upside as most of these can be executed individually as well.
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [dealerResults, setDealerResults] = useState<DealerResultType[]>(
        previouslySelectedDealer ? [previouslySelectedDealer] : [],
    );

    // fetch dealer data and update store
    const selectDealer = useCallback(async (dealerId: string) => {
        setLoading(true);

        const dealerResult = await fetch(dealerDetailEndpoint, {
            method: "POST",
            body: JSON.stringify({ dealerId, a2dContext }),
            headers: { "Content-Type": "application/json" },
        });

        const parsedDealer: DealerResultType = await dealerResult.json();
        if (parsedDealer) {
            onDealerSelect(parsedDealer);
        } else {
            Debug.warn(`Cannot find dealer with id ${dealerId}`);
            onDealerSelect(null);
        }
        setLoading(false);
        // Should only run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----------------------------------------------------------------------
    // Standalone input field logic
    // ----------------------------------------------------------------------

    const onSearchInputChange = (event: ChangeEvent<HTMLInputElement>): void => setSearchQuery(event.target.value);
    useDebouncedEffect(
        () => {
            if (!commonSettings.isStandalone) return;

            if (searchQuery.length > 2) {
                // Reset the related states.
                setLoading(true);
                setDealerResults([]);

                // Execute a dealer/location search.
                const searchDealers = async (): Promise<void> => {
                    const result = await fetch(dealerSearchEndpoint, {
                        method: "POST",
                        body: JSON.stringify({ query: searchQuery, a2dContext }),
                        headers: { "Content-Type": "application/json" },
                    });

                    if (result.status === 200) {
                        const parsedResult: { results: DealerResultType[] } = await result.json();
                        if (parsedResult) {
                            setDealerResults(parsedResult.results);
                        }
                    }

                    setLoading(false);
                };

                searchDealers();
            } else {
                // If the search query is 2 or less, reset the results as that means the user is deleting the text.
                // eslint-disable-next-line no-lonely-if
                if (dealerResults.length) setDealerResults(previouslySelectedDealer ? [previouslySelectedDealer] : []);
            }
        },
        500,
        [searchQuery, commonSettings.isStandalone],
    );

    if (!commonSettings.isStandalone) {
        return (
            <tme-dealerfinder
                services={serviceFilter}
                // When uuid is used it will automatically switch to searchtype="name"
                uuid={previouslySelectedDealer?.id}
                is-prefill-preferred-dealer="true"
                read-only-selected-services="true"
                searchtype="location"
                isdynamicmap="false"
                isdealerrating="false"
                show-not-matching-services="false"
                soft-filtering-disable-dealer="false"
                select-first-dealer="false"
                tiles-layout="XL"
                search-layout="inline"
                distance-unit="km"
                show-results-on-page-load={showResultsOnPageLoad ? "true" : "false"}
                mobile-map-last="false"
                limit-search-distance="0"
                disable-search-types="false"
                hide-services="true"
                hide-header="true"
                enableselect="true"
                disablesave="true"
                ondealererror={(error: Event) => Debug.warn("Dealer finder error", error)}
                ondealerselect={(data: { dealer: { id: string } }) => {
                    if (data?.dealer?.id) {
                        selectDealer(data.dealer.id);
                    } else {
                        Debug.warn("Did not receive a dealer id");
                    }
                }}
            />
        );
    }

    return (
        <div className="container mt-4 mb-5">
            <label>
                Manually select a dealer (standalone only):
                <input
                    type="text"
                    value={searchQuery}
                    placeholder="Search by name or location"
                    onChange={onSearchInputChange}
                    className="d-block mt-2 p-1"
                />
            </label>

            {loading && <div className="loading mt-3" />}

            {!!dealerResults.length && (
                <ul className="mt-3">
                    {dealerResults.map((dealer) => (
                        <li key={dealer.id}>
                            <button
                                type="button"
                                className={`text-left ${previouslySelectedDealer?.id === dealer.id ? "text-bold" : ""}`}
                                onClick={() => selectDealer(dealer.id)}
                            >
                                {dealer.name}
                                {` (${dealer.address.line1} ${dealer.address.zip} ${dealer.address.city})`}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AemDealerFinder;
